<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">角色管理</h1>
            <p class="list-page-description">统一管理角色、权限标识和数据范围配置</p>
          </div>
        </div>
      </div>
    </template>

    <!-- 表格区域 -->
    <div class="list-page-table-panel">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        show-refresh
        :refresh-loading="loading"
        @refresh="fetchRoles"
      >
        <input v-model="searchForm.name" type="text" placeholder="角色名称" class="form-input form-control-sm filter-bar-field">
        <input v-model="searchForm.roleKey" type="text" placeholder="权限字符" class="form-input form-control-sm filter-bar-field">
        <el-select v-model="searchForm.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部状态" value="" />
          <el-option label="启用" value="0" />
          <el-option label="禁用" value="1" />
        </el-select>

        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="resetSearch">重置</button>
        </template>

        <template #actions>
          <button v-permission="'system:role:add'" class="btn-primary" @click="openModal('add')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            新增
          </button>
          <button v-permission="'system:role:edit'" class="btn-outline-primary" @click="openPermissionConfigModal">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            数据权限配置
          </button>
        </template>
      </PageFilterBar>

      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th is-center w-10">
                  <input v-model="selectAll" type="checkbox" class="form-check" @change="toggleSelectAll">
                </th>
                <th class="data-table-th text-left">角色编号</th>
                <th class="data-table-th text-left">角色名称</th>
                <th class="data-table-th text-left">权限字符</th>
                <th class="data-table-th text-left">数据范围</th>
                <th class="data-table-th is-center">状态</th>
                <th class="data-table-th text-left">创建时间</th>
                <th class="data-table-th is-center">操作</th>
              </tr>
            </thead>
            <tbody class="data-table-body">
              <!-- 骨架屏加载状态 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td is-center"><div class="h-4 w-4 bg-slate-200 rounded mx-auto"></div></td>
                  <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td"><div class="h-4 w-20 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td"><div class="h-4 w-16 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center"><div class="h-6 w-14 bg-slate-200 rounded-full mx-auto"></div></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"></div></td>
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <div class="h-8 w-8 bg-slate-200 rounded"></div>
                      <div class="h-8 w-8 bg-slate-200 rounded"></div>
                    </div>
                  </td>
                </tr>
              </template>
              <!-- 空状态 -->
              <tr v-if="!loading && displayedRoles.length === 0">
                <td colspan="8" class="data-table-empty">暂无数据</td>
              </tr>
              <tr v-for="role in displayedRoles" :key="role.id" class="data-table-row">
                <td class="data-table-td is-center"><input v-model="role.selected" type="checkbox" class="form-check"></td>
                <td class="data-table-td">{{ role.id }}</td>
                <td class="data-table-td">{{ role.name }}</td>
                <td class="data-table-td">{{ role.roleKey }}</td>
                <td class="data-table-td">{{ formatDataScope(role.dataScope) }}</td>
                <td class="data-table-td is-center">
                  <span class="status-badge" :class="role.status === STATUS_ENABLED ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                    <span class="w-1.5 h-1.5 rounded-full mr-1" :class="role.status === STATUS_ENABLED ? 'bg-green-500' : 'bg-red-500'"></span>
                    {{ role.status === STATUS_ENABLED ? '启用' : '禁用' }}
                  </span>
                </td>
                <td class="data-table-td">{{ role.createdAt || '-' }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button v-permission="'system:role:edit'" class="table-action-button is-primary" title="修改" aria-label="修改" @click="openModal('edit', role)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button v-permission="'system:role:remove'" class="table-action-button is-danger" title="删除" aria-label="删除" @click="handleDeleteSingle(role)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        v-if="total > 0"
        class="pagination-bridge"
        :current="pageNum"
        :size="pageSize"
        :total="total"
        :pages="totalPage"
        :page-sizes="[10, 20, 50]"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 新增/编辑角色弹窗 -->
    <Teleport to="body">
      <div v-if="modalVisible" class="fixed inset-0 bg-black/50 z-50" @click.self="closeModal">
        <div class="flex items-center justify-center min-h-screen p-4">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <!-- 弹窗头部 -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 class="text-lg font-semibold text-slate-900">{{ modalType === 'add' ? '新增角色' : '修改角色' }}</h3>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closeModal">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <!-- 角色名称 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>角色名称
                    </label>
                    <input v-model="form.name" type="text" required
                      class="form-input w-full"
                      placeholder="请输入角色名称">
                  </div>
                  <!-- 权限字符 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>权限字符
                    </label>
                    <input v-model="form.roleKey" type="text" required
                      class="form-input w-full"
                      placeholder="请输入权限字符">
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <!-- 数据范围 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>数据范围
                    </label>
                    <el-select v-model="form.dataScope"
                      class="form-select w-full">
                      <el-option v-for="option in dataScopeOptions" :key="option.value" :label="option.label" :value="option.value" />
                    </el-select>
                  </div>
                  <!-- 状态 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">状态</label>
                    <el-select v-model="form.status"
                      class="form-select w-full">
                      <el-option label="启用" :value="STATUS_ENABLED" />
                      <el-option label="禁用" :value="STATUS_DISABLED" />
                    </el-select>
                  </div>
                </div>

                <!-- 菜单权限 -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium text-slate-700">菜单权限</label>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="menuCascade"
                        class="form-check"
                      >
                      <span class="text-sm text-slate-600">父子联动</span>
                    </label>
                  </div>
                  <div class="border border-slate-300 rounded-lg p-4 bg-slate-50 max-h-60 overflow-y-auto">
                    <div class="space-y-1">
                      <div v-if="flattenedMenuTree.length === 0" class="text-sm text-slate-500">暂无菜单数据</div>
                      <div v-for="item in flattenedMenuTree" :key="item.node.id" class="flex items-center gap-2 py-1" :style="{ paddingLeft: `${item.level * 16}px` }">
                        <button
                          v-if="item.node.children && item.node.children.length > 0"
                          type="button"
                          class="expand-btn p-0.5 hover:bg-slate-200 rounded transition-colors"
                          @click="toggleExpand(item.node.id)"
                        >
                          <svg
                            class="w-4 h-4 text-slate-500 transition-transform"
                            :style="{ transform: isExpanded(item.node.id) ? 'rotate(90deg)' : 'rotate(0deg)' }"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                          </svg>
                        </button>
                        <span v-else class="w-4 h-4"></span>
                        <label class="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            class="form-check"
                            :checked="isNodeChecked(item.node)"
                            :indeterminate.prop="isNodeIndeterminate(item.node)"
                            @change="onNodeCheck(item.node, $event)"
                          >
                          <span class="text-sm text-slate-700">{{ item.node.name }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 资源数据权限 -->
                <div>
                  <div class="flex items-center justify-between mb-3">
                    <label class="block text-sm font-medium text-slate-700">资源数据权限</label>
                    <span class="text-xs text-slate-500">为不同资源配置独立的数据范围</span>
                  </div>

                  <!-- 资源权限加载状态 -->
                  <div v-if="roleResourcesLoading" class="border border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
                    <div class="flex items-center justify-center gap-2">
                      <svg class="w-5 h-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      加载资源列表...
                    </div>
                  </div>

                  <!-- 空状态 -->
                  <div v-else-if="roleResourceScopes.length === 0" class="border border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
                    暂无可配置的资源
                  </div>

                  <!-- 资源权限表格 -->
                  <div v-else class="border border-slate-300 rounded-lg overflow-hidden">
                    <div class="overflow-x-auto max-h-96 overflow-y-auto">
                      <table class="w-full">
                        <thead class="bg-slate-50 sticky top-0 z-10">
                          <tr class="border-b border-slate-200">
                            <th class="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">资源名称</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">全部数据</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">本部门</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">本部门及以下</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">仅本人</th>
                            <th class="px-4 py-3 text-center text-xs font-medium text-slate-700 uppercase tracking-wider whitespace-nowrap">自定义</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-slate-200">
                          <template v-for="(resource, index) in roleResourceScopes" :key="resource.resourceCode">
                            <tr class="hover:bg-slate-50 transition-colors">
                              <td class="px-4 py-3">
                                <div class="flex flex-col">
                                  <span class="text-sm font-medium text-slate-900">{{ resource.resourceName }}</span>
                                  <span class="text-xs text-slate-500 font-mono">{{ resource.resourceCode }}</span>
                                </div>
                              </td>
                              <td class="px-4 py-3 text-center">
                                <label class="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="radio"
                                    :name="`resource-${resource.resourceCode}`"
                                    :value="1"
                                    v-model.number="resource.dataScope"
                                    @change="onResourceDataScopeChange(index)"
                                    class="form-radio"
                                  >
                                </label>
                              </td>
                              <td class="px-4 py-3 text-center">
                                <label class="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="radio"
                                    :name="`resource-${resource.resourceCode}`"
                                    :value="2"
                                    v-model.number="resource.dataScope"
                                    @change="onResourceDataScopeChange(index)"
                                    class="form-radio"
                                  >
                                </label>
                              </td>
                              <td class="px-4 py-3 text-center">
                                <label class="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="radio"
                                    :name="`resource-${resource.resourceCode}`"
                                    :value="3"
                                    v-model.number="resource.dataScope"
                                    @change="onResourceDataScopeChange(index)"
                                    class="form-radio"
                                  >
                                </label>
                              </td>
                              <td class="px-4 py-3 text-center">
                                <label class="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="radio"
                                    :name="`resource-${resource.resourceCode}`"
                                    :value="4"
                                    v-model.number="resource.dataScope"
                                    @change="onResourceDataScopeChange(index)"
                                    class="form-radio"
                                  >
                                </label>
                              </td>
                              <td class="px-4 py-3 text-center">
                                <label class="inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="radio"
                                    :name="`resource-${resource.resourceCode}`"
                                    :value="5"
                                    v-model.number="resource.dataScope"
                                    @change="onResourceDataScopeChange(index)"
                                    class="form-radio"
                                  >
                                </label>
                              </td>
                            </tr>
                            <!-- 自定义部门选择行 -->
                            <tr v-if="resource.dataScope === 5" class="bg-slate-50">
                              <td colspan="6" class="px-4 py-3">
                                <div class="flex items-center gap-2">
                                  <span class="text-sm text-slate-600 whitespace-nowrap">选择部门：</span>
                                  <button
                                    type="button"
                                    class="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-left hover:bg-white hover:border-primary-400 transition-colors flex items-center justify-between group bg-white"
                                    @click="openDeptSelector(index)">
                                    <span class="truncate" :class="resource.selectedDeptNames ? 'text-slate-900' : 'text-slate-400'">
                                      {{ resource.selectedDeptNames || '点击选择部门' }}
                                    </span>
                                    <svg class="w-4 h-4 text-slate-400 group-hover:text-primary-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                    </svg>
                                  </button>
                                  <span v-if="resource.selectedDeptNames" class="text-xs text-slate-500 whitespace-nowrap">
                                    已选 {{ resource.deptIds.length }} 个
                                  </span>
                                </div>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </form>
            </div>

            <!-- 弹窗底部 -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 transition-colors" @click="closeModal">
                取消
              </button>
              <button type="button" class="btn-primary" @click="saveRole">
                确定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 删除确认弹窗 -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="modal-backdrop absolute inset-0" style="background-color: rgba(0, 0, 0, 0.5);" @click="closeDeleteModal"></div>
      <div class="modal-content relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4" style="animation: slideIn 0.2s ease-out;">
        <div class="p-6 text-center">
          <div class="w-12 h-12 mx-auto mb-4 text-red-500">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-slate-900 mb-2">确定要删除吗？</h3>
          <p class="text-sm text-slate-500 mb-6">{{ deleteHint }}</p>
          <div class="flex items-center justify-center gap-3">
            <button @click="closeDeleteModal" class="btn-secondary">取消</button>
            <button @click="confirmDelete" class="btn-danger-secondary">确定删除</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 权限配置弹窗 -->
    <Teleport to="body">
      <div v-if="permissionConfigVisible" class="fixed inset-0 bg-black/50 z-50" @click.self="closePermissionConfigModal">
        <div class="flex items-center justify-center min-h-screen p-4">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <!-- 弹窗头部 -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">数据权限资源配置</h3>
                <p class="text-sm text-slate-500 mt-0.5">配置系统中的数据权限资源及其字段映射关系</p>
              </div>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closePermissionConfigModal">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <!-- 添加资源按钮 -->
              <div class="mb-4">
                <button class="btn-primary" @click="openResourceModal('add')">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  添加资源
                </button>
              </div>

              <!-- 资源列表表格 -->
              <div class="border border-slate-200 rounded-lg overflow-hidden">
                <table class="w-full">
                  <thead>
                    <tr class="bg-slate-50 border-b border-slate-200">
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">资源编码</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">资源名称</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">部门字段</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">用户字段</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">排序</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">状态</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-32">操作</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200">
                    <!-- 加载状态 -->
                    <tr v-if="resourcesLoading">
                      <td colspan="7" class="px-4 py-8 text-center text-sm text-slate-500">
                        <div class="flex items-center justify-center gap-2">
                          <svg class="w-5 h-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          加载中...
                        </div>
                      </td>
                    </tr>
                    <!-- 空状态 -->
                    <tr v-else-if="resourcesList.length === 0">
                      <td colspan="7" class="px-4 py-8 text-center text-sm text-slate-500">暂无数据权限资源配置</td>
                    </tr>
                    <!-- 数据列表 -->
                    <tr v-for="resource in resourcesList" :key="resource.id" class="hover:bg-slate-50 cursor-pointer transition-colors">
                      <td class="px-4 py-3 text-sm text-slate-900 font-mono">{{ resource.resourceCode }}</td>
                      <td class="px-4 py-3 text-sm text-slate-900">{{ resource.resourceName }}</td>
                      <td class="px-4 py-3 text-sm text-slate-600 font-mono">{{ resource.deptColumn || '-' }}</td>
                      <td class="px-4 py-3 text-sm text-slate-600 font-mono">{{ resource.userColumn || '-' }}</td>
                      <td class="px-4 py-3 text-sm text-slate-600">{{ resource.sort || 0 }}</td>
                      <td class="px-4 py-3">
                        <span class="status-badge" :class="resource.status === STATUS_ENABLED ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                          <span class="w-1.5 h-1.5 rounded-full mr-1" :class="resource.status === STATUS_ENABLED ? 'bg-green-500' : 'bg-red-500'"></span>
                          {{ resource.status === STATUS_ENABLED ? '启用' : '禁用' }}
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-1">
                          <button class="table-action-button is-primary" title="编辑" aria-label="编辑" @click="openResourceModal('edit', resource)">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </button>
                          <button class="table-action-button is-danger" title="删除" aria-label="删除" @click="handleDeleteResource(resource)">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 弹窗底部 -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 transition-colors" @click="closePermissionConfigModal">
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 资源配置表单弹窗 -->
    <Teleport to="body">
      <div v-if="resourceModalVisible" class="fixed inset-0 bg-black/50 z-[60]" @click.self="closeResourceModal">
        <div class="flex items-center justify-center min-h-screen p-4">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <!-- 弹窗头部 -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 class="text-lg font-semibold text-slate-900">{{ resourceModalType === 'add' ? '添加资源' : '编辑资源' }}</h3>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closeResourceModal">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <!-- 资源编码 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>资源编码
                    </label>
                    <input v-model="resourceForm.resourceCode" type="text" required
                      class="form-input w-full font-mono"
                      placeholder="例如: customer">
                  </div>
                  <!-- 资源名称 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>资源名称
                    </label>
                    <input v-model="resourceForm.resourceName" type="text" required
                      class="form-input w-full"
                      placeholder="例如: 客户">
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <!-- 部门字段 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      部门字段名
                    </label>
                    <input v-model="resourceForm.deptColumn" type="text"
                      class="form-input w-full font-mono"
                      placeholder="例如: owner_dept_id">
                    <p class="text-xs text-slate-500 mt-1">数据库中关联部门的字段名</p>
                  </div>
                  <!-- 用户字段 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      用户字段名
                    </label>
                    <input v-model="resourceForm.userColumn" type="text"
                      class="form-input w-full font-mono"
                      placeholder="例如: owner_user_id">
                    <p class="text-xs text-slate-500 mt-1">数据库中关联用户的字段名</p>
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <!-- 排序号 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">排序号</label>
                    <input v-model.number="resourceForm.sort" type="number"
                      class="form-input w-full"
                      placeholder="0">
                  </div>
                  <!-- 状态 -->
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">状态</label>
                    <el-select v-model="resourceForm.status"
                      class="form-select w-full">
                      <el-option label="启用" :value="STATUS_ENABLED" />
                      <el-option label="禁用" :value="STATUS_DISABLED" />
                    </el-select>
                  </div>
                </div>

                <!-- 资源描述 -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1">资源描述</label>
                  <textarea v-model="resourceForm.description" rows="3"
                    class="form-textarea"
                    placeholder="请输入资源描述（可选）"></textarea>
                </div>
              </form>
            </div>

            <!-- 弹窗底部 -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 transition-colors" @click="closeResourceModal">
                取消
              </button>
              <button type="button" class="btn-primary" @click="saveResource">
                确定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 部门选择器弹窗 -->
    <Teleport to="body">
      <div v-if="deptSelectorVisible" class="fixed inset-0 bg-black/50 z-[70]" @click.self="closeDeptSelector">
        <div class="flex items-center justify-center min-h-screen p-4">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
            <!-- 弹窗头部 -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 class="text-lg font-semibold text-slate-900">选择部门</h3>
              <button class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" @click="closeDeptSelector">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div class="border border-slate-300 rounded-lg p-4 bg-slate-50">
                <div v-if="deptTreeLoading" class="text-center text-sm text-slate-500 py-4">
                  <div class="flex items-center justify-center gap-2">
                    <svg class="w-4 h-4 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    加载中...
                  </div>
                </div>
                <div v-else-if="deptTree.length === 0" class="text-sm text-slate-500 text-center py-4">
                  暂无部门数据
                </div>
                <div v-else class="space-y-1">
                  <div v-for="item in flattenedDeptTree" :key="item.node.id" class="flex items-center gap-2 py-1" :style="{ paddingLeft: `${item.level * 16}px` }">
                    <button
                      v-if="item.node.children && item.node.children.length > 0"
                      type="button"
                      class="p-0.5 hover:bg-slate-200 rounded transition-colors"
                      @click="toggleDeptExpand(item.node.id)"
                    >
                      <svg
                        class="w-4 h-4 text-slate-500 transition-transform"
                        :style="{ transform: isDeptExpanded(item.node.id) ? 'rotate(90deg)' : 'rotate(0deg)' }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                    <span v-else class="w-4 h-4"></span>
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        class="form-check"
                        :checked="selectedDeptIds.includes(item.node.id)"
                        @change="onDeptCheck(item.node.id, $event)"
                      >
                      <span class="text-sm text-slate-700">{{ item.node.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- 弹窗底部 -->
            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 transition-colors" @click="closeDeptSelector">
                取消
              </button>
              <button type="button" class="btn-primary" @click="confirmDeptSelection">
                确定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getRoles, getRole, createRole, updateRole, deleteRole, getAllDataScopeResources, saveDataScopeResource, deleteDataScopeResource } from '@/api/role'
import { getMenuTree } from '@/api/menu'
import { getDeptTree } from '@/api/dept'
import PageFilterBar from '@/components/PageFilterBar.vue'

// 搜索表单
const STATUS_ENABLED = 0
const STATUS_DISABLED = 1

const dataScopeOptions = [
  { value: 1, label: '全部' },
  { value: 2, label: '本部门' },
  { value: 3, label: '本部门及以下' },
  { value: 4, label: '自定义' }
]

const searchForm = reactive({
  name: '',
  roleKey: '',
  status: ''
})

const roleList = ref([])
const loading = ref(false)

// 分页
const pageNum = ref(1)
const pageSize = ref(10)
const total = computed(() => roleList.value.length)
const totalPage = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
const displayedRoles = computed(() => {
  const start = (pageNum.value - 1) * pageSize.value
  return roleList.value.slice(start, start + pageSize.value)
})

// 分页处理
const handlePageChange = (p) => {
  pageNum.value = p
}

const handlePageSizeChange = (s) => {
  pageSize.value = s
  pageNum.value = 1
}

// 扁平化菜单树（用于显示）
const flattenedMenuTree = computed(() => {
  const result = []
  const traverse = (nodes, level) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      result.push({ node, level })
      if (node.children && Array.isArray(node.children) && expandedMenuIds.value.includes(node.id)) {
        traverse(node.children, level + 1)
      }
    })
  }
  if (Array.isArray(menuTree.value)) {
    traverse(menuTree.value, 0)
  }
  return result
})

// 全选
const selectAll = ref(false)

const toggleSelectAll = () => {
  roleList.value.forEach(role => {
    role.selected = selectAll.value
  })
}

// 弹窗相关
const modalVisible = ref(false)
const modalType = ref('add')
const currentRole = ref(null)

// 表单数据
const form = reactive({
  name: '',
  roleKey: '',
  dataScope: 1,
  status: STATUS_ENABLED
})

// 菜单权限
const menuTree = ref([])
const expandedMenuIds = ref([])
const selectedMenuIds = ref([]) // 完全选中的节点
const halfSelectedMenuIds = ref([]) // 半选中的节点（部分子节点选中）
const menuTreeLoaded = ref(false)
const menuCascade = ref(false) // 父子联动开关

// 检查节点是否有子节点被选中
const hasChildSelected = (node) => {
  if (selectedMenuIds.value.includes(node.id)) return true
  if (halfSelectedMenuIds.value.includes(node.id)) return true
  if (node.children && Array.isArray(node.children) && node.children.length > 0) {
    return node.children.some(child => hasChildSelected(child))
  }
  return false
}

// 更新单个节点的选中状态
const updateNodeState = (node) => {
  if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
    return
  }

  const checkedChildren = node.children.filter(child => selectedMenuIds.value.includes(child.id))
  const allChildren = node.children.filter(child => hasChildSelected(child))

  if (checkedChildren.length === node.children.length) {
    // 所有子节点都选中，移除半选状态，设置为完全选中
    halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== node.id)
    if (!selectedMenuIds.value.includes(node.id)) {
      selectedMenuIds.value.push(node.id)
    }
  } else if (allChildren.length > 0) {
    // 部分子节点选中，添加半选状态
    if (!halfSelectedMenuIds.value.includes(node.id)) {
      halfSelectedMenuIds.value.push(node.id)
    }
    selectedMenuIds.value = selectedMenuIds.value.filter(id => id !== node.id)
  } else {
    // 没有子节点选中，移除选中状态
    selectedMenuIds.value = selectedMenuIds.value.filter(id => id !== node.id)
    halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== node.id)
  }
}


// 更新所有父节点的选中状态（从叶子到根的递归顺序）
const updateParentStates = () => {
  // 递归更新，先更新子节点再更新父节点
  const updateRecursively = (nodes) => {
    if (!Array.isArray(nodes)) return
    for (const node of nodes) {
      if (node.children && Array.isArray(node.children)) {
        // 先递归更新所有子节点
        updateRecursively(node.children)
        // 再更新当前节点
        updateNodeState(node)
      }
    }
  }
  updateRecursively(menuTree.value)
}

// 查找从根到目标节点的祖先路径
const findNodeAncestors = (targetId) => {
  const ancestors = []
  const find = (nodes, path) => {
    if (!Array.isArray(nodes)) return false
    for (const node of nodes) {
      if (node.id === targetId) {
        ancestors.push(...path)
        return true
      }
      if (node.children && Array.isArray(node.children)) {
        if (find(node.children, [...path, node])) {
          return true
        }
      }
    }
    return false
  }
  find(menuTree.value, [])
  return ancestors
}

// 只更新当前操作节点的祖先链状态（不影响其他分支）
const updateAncestorStates = (targetId) => {
  const ancestors = findNodeAncestors(targetId)
  // 从最近的父节点向上逐层更新
  for (let i = ancestors.length - 1; i >= 0; i--) {
    updateNodeState(ancestors[i])
  }
}

// 检查节点是否被选中（包括完全选中或半选中）
const isNodeChecked = (node) => {
  return selectedMenuIds.value.includes(node.id) || halfSelectedMenuIds.value.includes(node.id)
}

// 检查节点是否处于半选状态（部分子节点选中）
const isNodeIndeterminate = (node) => {
  return halfSelectedMenuIds.value.includes(node.id)
}

// 检查节点是否展开
const isExpanded = (menuId) => expandedMenuIds.value.includes(menuId)

// 切换节点展开/收起
const toggleExpand = (menuId) => {
  if (expandedMenuIds.value.includes(menuId)) {
    expandedMenuIds.value = expandedMenuIds.value.filter(id => id !== menuId)
  } else {
    expandedMenuIds.value = [...expandedMenuIds.value, menuId]
  }
}

// 设置节点选中状态（联动模式）
const setNodeCheckedCascade = (node, checked) => {
  if (checked) {
    // 选中当前节点
    if (!selectedMenuIds.value.includes(node.id)) {
      selectedMenuIds.value.push(node.id)
    }
    // 移除半选状态
    halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== node.id)

    // 选中所有子节点
    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      const selectAllChildren = (children) => {
        if (!Array.isArray(children)) return
        children.forEach(child => {
          if (!selectedMenuIds.value.includes(child.id)) {
            selectedMenuIds.value.push(child.id)
          }
          halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== child.id)
          if (child.children && Array.isArray(child.children) && child.children.length > 0) {
            selectAllChildren(child.children)
          }
        })
      }
      selectAllChildren(node.children)
    }
  } else {
    // 取消选中当前节点
    selectedMenuIds.value = selectedMenuIds.value.filter(id => id !== node.id)
    halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== node.id)

    // 取消选中所有子节点
    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      const unselectAllChildren = (children) => {
        if (!Array.isArray(children)) return
        children.forEach(child => {
          selectedMenuIds.value = selectedMenuIds.value.filter(id => id !== child.id)
          halfSelectedMenuIds.value = halfSelectedMenuIds.value.filter(id => id !== child.id)
          if (child.children && Array.isArray(child.children) && child.children.length > 0) {
            unselectAllChildren(child.children)
          }
        })
      }
      unselectAllChildren(node.children)
    }
  }

  // 只更新当前操作节点的祖先链，不影响其他分支
  updateAncestorStates(node.id)
}

const onNodeCheck = (node, event) => {
  const checked = event.target.checked
  if (menuCascade.value) {
    // 父子联动模式
    setNodeCheckedCascade(node, checked)
  } else {
    // 非联动模式：只影响当前节点
    const selected = new Set(selectedMenuIds.value)
    const halfSelected = new Set(halfSelectedMenuIds.value)
    if (checked) {
      selected.add(node.id)
      halfSelected.delete(node.id)
    } else {
      selected.delete(node.id)
      halfSelected.delete(node.id)
    }
    selectedMenuIds.value = Array.from(selected)
    halfSelectedMenuIds.value = Array.from(halfSelected)
  }
}

const resetMenuSelection = () => {
  selectedMenuIds.value = []
  halfSelectedMenuIds.value = []
}

const applyMenuSelection = (menuIds = []) => {
  const menuIdSet = new Set(menuIds)
  selectedMenuIds.value = []
  halfSelectedMenuIds.value = []

  // 选中 menuIds 中存在的节点
  const traverse = (item) => {
    if (menuIdSet.has(item.id) && Number.isFinite(item.id)) {
      selectedMenuIds.value.push(item.id)
    }
    if (item.children && Array.isArray(item.children) && item.children.length > 0) {
      item.children.forEach(traverse)
    }
  }
  if (Array.isArray(menuTree.value)) {
    menuTree.value.forEach(traverse)
  }

  // 父子联动开启时才根据子节点反推父级半选状态；默认关闭时严格回显后端返回的权限。
  if (menuCascade.value) {
    updateParentStates()
  }
}

const collectCheckedMenuIds = () => {
  // 返回完全选中 + 半选中的所有节点ID
  const allSelected = new Set([...selectedMenuIds.value, ...halfSelectedMenuIds.value])
  return Array.from(allSelected)
}

const collectExpandedIds = (nodes) => {
  const ids = []
  if (!Array.isArray(nodes)) return ids
  nodes.forEach(node => {
    if (node.children && Array.isArray(node.children) && node.children.length > 0 && Number.isFinite(node.id)) {
      ids.push(node.id)
      ids.push(...collectExpandedIds(node.children))
    }
  })
  return ids
}

const fetchMenuTree = async () => {
  if (menuTreeLoaded.value) return
  try {
    const data = await getMenuTree()
    menuTree.value = Array.isArray(data) ? data : []
    expandedMenuIds.value = collectExpandedIds(menuTree.value)
    menuTreeLoaded.value = true
  } catch (error) {
    ElMessage.error(error.message || '获取菜单树失败')
  }
}

// 打开弹窗
const openModal = async (type, role) => {
  modalType.value = type
  modalVisible.value = true

  // 编辑时重新获取最新的菜单树
  if (type === 'edit') {
    menuTreeLoaded.value = false
  }
  await fetchMenuTree()

  if (type === 'add') {
    form.name = ''
    form.roleKey = ''
    form.dataScope = 1
    form.status = STATUS_ENABLED
    resetMenuSelection()
    // 加载资源权限配置（新增时使用默认值）
    await loadRoleResourceScopes(null)
  } else if (type === 'edit' && role) {
    currentRole.value = role
    try {
      const detail = await getRole(role.id)
      form.name = detail?.name || role.name
      form.roleKey = detail?.roleKey || role.roleKey
      form.dataScope = detail?.dataScope ?? role.dataScope ?? 1
      form.status = detail?.status ?? role.status ?? STATUS_ENABLED
      applyMenuSelection(detail?.menuIds || [])
      // 加载资源权限配置（编辑时加载角色的资源配置）
      await loadRoleResourceScopes(role.id)
    } catch (error) {
      form.name = role.name
      form.roleKey = role.roleKey
      form.dataScope = role.dataScope ?? 1
      form.status = role.status ?? STATUS_ENABLED
      applyMenuSelection(role.menuIds || [])
      await loadRoleResourceScopes(role.id)
      ElMessage.error(error.message || '获取角色详情失败')
    }
  }
}

// 关闭弹窗
const closeModal = () => {
  modalVisible.value = false
  currentRole.value = null
}

// 保存角色
const saveRole = async () => {
  if (!form.name.trim()) {
    ElMessage.warning('请输入角色名称')
    return
  }
  if (!form.roleKey.trim()) {
    ElMessage.warning('请输入权限字符')
    return
  }

  // 构建资源数据权限配置
  const resourceDataScopes = roleResourceScopes.value.map(resource => ({
    resourceCode: resource.resourceCode,
    dataScope: resource.dataScope,
    deptIds: resource.dataScope === 5 ? (resource.deptIds || []) : []
  }))

  const payload = {
    name: form.name.trim(),
    roleKey: form.roleKey.trim(),
    dataScope: form.dataScope,
    status: Number(form.status),
    menuIds: collectCheckedMenuIds(),
    resourceDataScopes: resourceDataScopes
  }

  try {
    if (modalType.value === 'add') {
      await createRole(payload)
      ElMessage.success('新增成功')
    } else if (currentRole.value) {
      await updateRole(currentRole.value.id, { id: currentRole.value.id, ...payload })
      ElMessage.success('修改成功')
    }
    await fetchRoles()
    closeModal()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 搜索
const handleSearch = async () => {
  pageNum.value = 1
  await fetchRoles()
}

// 重置搜索
const resetSearch = async () => {
  searchForm.name = ''
  searchForm.roleKey = ''
  searchForm.status = ''
  pageNum.value = 1
  await fetchRoles()
}

// 单个删除
const handleDeleteSingle = (role) => {
  openDeleteModal([role])
}

const showDeleteModal = ref(false)
const pendingDeleteRoles = ref([])

const deleteHint = computed(() => {
  if (pendingDeleteRoles.value.length === 1) {
    return `将删除角色 "${pendingDeleteRoles.value[0].name}"，删除后无法恢复。`
  }
  return `将删除 ${pendingDeleteRoles.value.length} 个角色，删除后无法恢复。`
})

const openDeleteModal = (roles) => {
  pendingDeleteRoles.value = roles
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  pendingDeleteRoles.value = []
}

const confirmDelete = async () => {
  const ids = pendingDeleteRoles.value.map(role => role.id)
  if (ids.length === 0) {
    closeDeleteModal()
    return
  }
  try {
    await Promise.all(ids.map(id => deleteRole(id)))
    ElMessage.success('删除成功')
    await fetchRoles()
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  } finally {
    closeDeleteModal()
  }
}

const formatDataScope = (value) => {
  const option = dataScopeOptions.find(item => item.value === value)
  return option ? option.label : '-'
}

const fetchRoles = async () => {
  loading.value = true
  const params = {
    name: searchForm.name.trim(),
    roleKey: searchForm.roleKey.trim()
  }
  if (searchForm.status !== '') {
    params.status = Number(searchForm.status)
  }
  try {
    const data = await getRoles(params)
    const list = Array.isArray(data) ? data : []
    roleList.value = list.map(role => ({ ...role, selected: false }))
    selectAll.value = false
  } catch (error) {
    ElMessage.error(error.message || '获取角色列表失败')
  } finally {
    loading.value = false
  }
}

watch([total, pageSize], () => {
  if (pageNum.value > totalPage.value) {
    pageNum.value = totalPage.value
  }
})

watch(pageSize, () => {
  pageNum.value = 1
})

// 权限配置相关
const permissionConfigVisible = ref(false)
const resourcesList = ref([])
const resourcesLoading = ref(false)

// 资源表单弹窗
const resourceModalVisible = ref(false)
const resourceModalType = ref('add')
const currentResource = ref(null)

const resourceForm = reactive({
  resourceCode: '',
  resourceName: '',
  deptColumn: '',
  userColumn: '',
  description: '',
  sort: 0,
  status: STATUS_ENABLED
})

// 打开权限配置弹窗
const openPermissionConfigModal = async () => {
  permissionConfigVisible.value = true
  await fetchResourcesList()
}

// 关闭权限配置弹窗
const closePermissionConfigModal = () => {
  permissionConfigVisible.value = false
}

// 获取资源列表
const fetchResourcesList = async () => {
  resourcesLoading.value = true
  try {
    const data = await getAllDataScopeResources()
    resourcesList.value = Array.isArray(data) ? data : []
  } catch (error) {
    ElMessage.error(error.message || '获取资源列表失败')
  } finally {
    resourcesLoading.value = false
  }
}

// 打开资源表单弹窗
const openResourceModal = (type, resource) => {
  resourceModalType.value = type
  resourceModalVisible.value = true

  if (type === 'add') {
    resourceForm.resourceCode = ''
    resourceForm.resourceName = ''
    resourceForm.deptColumn = ''
    resourceForm.userColumn = ''
    resourceForm.description = ''
    resourceForm.sort = 0
    resourceForm.status = STATUS_ENABLED
    currentResource.value = null
  } else if (type === 'edit' && resource) {
    currentResource.value = resource
    resourceForm.resourceCode = resource.resourceCode || ''
    resourceForm.resourceName = resource.resourceName || ''
    resourceForm.deptColumn = resource.deptColumn || ''
    resourceForm.userColumn = resource.userColumn || ''
    resourceForm.description = resource.description || ''
    resourceForm.sort = resource.sort || 0
    resourceForm.status = resource.status ?? STATUS_ENABLED
  }
}

// 关闭资源表单弹窗
const closeResourceModal = () => {
  resourceModalVisible.value = false
  currentResource.value = null
}

// 保存资源
const saveResource = async () => {
  if (!resourceForm.resourceCode.trim()) {
    ElMessage.warning('请输入资源编码')
    return
  }
  if (!resourceForm.resourceName.trim()) {
    ElMessage.warning('请输入资源名称')
    return
  }

  const payload = {
    resourceCode: resourceForm.resourceCode.trim(),
    resourceName: resourceForm.resourceName.trim(),
    deptColumn: resourceForm.deptColumn.trim(),
    userColumn: resourceForm.userColumn.trim(),
    description: resourceForm.description.trim(),
    sort: Number(resourceForm.sort) || 0,
    status: Number(resourceForm.status)
  }

  // 如果是编辑模式，添加 ID
  if (resourceModalType.value === 'edit' && currentResource.value) {
    payload.id = currentResource.value.id
  }

  try {
    await saveDataScopeResource(payload)
    ElMessage.success(resourceModalType.value === 'add' ? '添加成功' : '修改成功')
    await fetchResourcesList()
    closeResourceModal()
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  }
}

// 删除资源
const handleDeleteResource = async (resource) => {
  if (!resource || !resource.id) return

  try {
    await ElMessageBox.confirm(
      `确定要删除资源 "${resource.resourceName}" 吗？删除后无法恢复。`,
      '删除确认',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }
    )

    await deleteDataScopeResource(resource.id)
    ElMessage.success('删除成功')
    await fetchResourcesList()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '删除失败')
    }
  }
}

// 角色资源权限配置
const roleResourcesLoading = ref(false)
const roleResourceScopes = ref([])

// 部门选择器
const deptSelectorVisible = ref(false)
const deptTree = ref([])
const deptTreeLoading = ref(false)
const expandedDeptIds = ref([])
const selectedDeptIds = ref([])
const currentResourceIndex = ref(-1)

// 扁平化部门树
const flattenedDeptTree = computed(() => {
  const result = []
  const traverse = (nodes, level) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      result.push({ node, level })
      if (node.children && Array.isArray(node.children) && expandedDeptIds.value.includes(node.id)) {
        traverse(node.children, level + 1)
      }
    })
  }
  if (Array.isArray(deptTree.value)) {
    traverse(deptTree.value, 0)
  }
  return result
})

// 获取部门树
const fetchDeptTree = async () => {
  if (deptTree.value.length > 0) return
  deptTreeLoading.value = true
  try {
    const data = await getDeptTree()
    deptTree.value = Array.isArray(data) ? data : []
    // 自动展开所有节点
    expandedDeptIds.value = collectAllDeptIds(deptTree.value)
  } catch (error) {
    ElMessage.error(error.message || '获取部门树失败')
  } finally {
    deptTreeLoading.value = false
  }
}

// 收集所有部门ID
const collectAllDeptIds = (nodes) => {
  const ids = []
  if (!Array.isArray(nodes)) return ids
  nodes.forEach(node => {
    if (node.id) ids.push(node.id)
    if (node.children && Array.isArray(node.children)) {
      ids.push(...collectAllDeptIds(node.children))
    }
  })
  return ids
}

// 切换部门展开
const toggleDeptExpand = (deptId) => {
  if (expandedDeptIds.value.includes(deptId)) {
    expandedDeptIds.value = expandedDeptIds.value.filter(id => id !== deptId)
  } else {
    expandedDeptIds.value = [...expandedDeptIds.value, deptId]
  }
}

// 检查部门是否展开
const isDeptExpanded = (deptId) => expandedDeptIds.value.includes(deptId)

// 部门复选框处理
const onDeptCheck = (deptId, event) => {
  const checked = event.target.checked
  if (checked) {
    if (!selectedDeptIds.value.includes(deptId)) {
      selectedDeptIds.value.push(deptId)
    }
  } else {
    selectedDeptIds.value = selectedDeptIds.value.filter(id => id !== deptId)
  }
}

// 打开部门选择器
const openDeptSelector = async (index) => {
  currentResourceIndex.value = index
  const resource = roleResourceScopes.value[index]
  selectedDeptIds.value = resource.deptIds ? [...resource.deptIds] : []

  await fetchDeptTree()
  deptSelectorVisible.value = true
}

// 关闭部门选择器
const closeDeptSelector = () => {
  deptSelectorVisible.value = false
  currentResourceIndex.value = -1
  selectedDeptIds.value = []
}

// 确认部门选择
const confirmDeptSelection = () => {
  if (currentResourceIndex.value >= 0) {
    const resource = roleResourceScopes.value[currentResourceIndex.value]
    resource.deptIds = [...selectedDeptIds.value]
    updateResourceDeptNames(currentResourceIndex.value)
  }
  closeDeptSelector()
}

// 更新资源的部门名称显示
const updateResourceDeptNames = (index) => {
  const resource = roleResourceScopes.value[index]
  if (!resource.deptIds || resource.deptIds.length === 0) {
    resource.selectedDeptNames = ''
    return
  }

  const deptNames = []
  const findDeptNames = (nodes) => {
    if (!Array.isArray(nodes)) return
    nodes.forEach(node => {
      if (resource.deptIds.includes(node.id)) {
        deptNames.push(node.name)
      }
      if (node.children && Array.isArray(node.children)) {
        findDeptNames(node.children)
      }
    })
  }
  findDeptNames(deptTree.value)
  resource.selectedDeptNames = deptNames.join(', ')
}

// 资源数据范围变化处理
const onResourceDataScopeChange = (index) => {
  const resource = roleResourceScopes.value[index]
  if (resource.dataScope !== 5) {
    // 如果不是自定义，清空部门选择
    resource.deptIds = []
    resource.selectedDeptNames = ''
  }
}

// 加载角色的资源权限配置
const loadRoleResourceScopes = async (roleId) => {
  roleResourcesLoading.value = true
  try {
    // 获取所有启用的资源
    const allResources = await getAllDataScopeResources()
    const enabledResources = Array.isArray(allResources)
      ? allResources.filter(r => r.status === STATUS_ENABLED)
      : []

    // 如果是编辑模式，获取角色详情中的资源配置
    let roleResourceData = []
    if (roleId) {
      try {
        const roleDetail = await getRole(roleId)
        roleResourceData = roleDetail?.resourceDataScopes || []
      } catch (error) {
        console.error('获取角色资源配置失败:', error)
      }
    }

    // 合并资源列表和角色配置
    roleResourceScopes.value = enabledResources.map(resource => {
      const existingConfig = roleResourceData.find(r => r.resourceCode === resource.resourceCode)
      return {
        resourceCode: resource.resourceCode,
        resourceName: resource.resourceName,
        dataScope: existingConfig?.dataScope || 1, // 默认全部数据
        deptIds: existingConfig?.deptIds || [],
        selectedDeptNames: ''
      }
    })

    // 如果有部门选择，加载部门树并更新部门名称
    if (roleResourceScopes.value.some(r => r.deptIds && r.deptIds.length > 0)) {
      await fetchDeptTree()
      roleResourceScopes.value.forEach((resource, index) => {
        if (resource.deptIds && resource.deptIds.length > 0) {
          updateResourceDeptNames(index)
        }
      })
    }
  } catch (error) {
    ElMessage.error(error.message || '加载资源配置失败')
  } finally {
    roleResourcesLoading.value = false
  }
}

onMounted(() => {
  fetchRoles()
})
</script>
